'use client';

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IntegrationCard, RequestIntegrationDialog } from '@/components/integrations';
import { useIntegrationsList, useDeleteIntegration } from '@/lib/api/integrations';
import { useRxIntegrationStatus } from '@/lib/api/rx';
import { useToast } from '@/components/ui/use-toast';
import {
  INTEGRATION_CATEGORIES,
  type IntegrationProvider,
} from '@/lib/integrations-constants';
import { useIntegrationsMetadata } from '@/hooks/use-integrations-metadata';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { CryptoPaymentHelper } from '@/components/ui/crypto-payment-helper';
import { isTagadaPay } from '@/config/branding';

/**
 * Integrations list page
 * Displays all available integrations in a grid layout with filtering
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(IntegrationsPage, PERMISSIONS.STORES_VIEW);

function IntegrationsPage() {
  const { storeRoutes, navigate, id } = useStoreNavigate();
  const { t } = useTranslation(['integrations', 'stores', 'common']);
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  // Get initial category from URL query param (e.g., ?category=storefront)
  const categoryFromUrl = searchParams.get('category');
  const validCategoryIds = INTEGRATION_CATEGORIES.map((c) => c.id) as string[];
  const initialCategory =
    categoryFromUrl && validCategoryIds.includes(categoryFromUrl) ? categoryFromUrl : 'all';

  // State
  const [selectedCategory, setSelectedCategory] = React.useState<string>(initialCategory);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
  const [disconnectDialog, setDisconnectDialog] = React.useState<{
    open: boolean;
    provider: IntegrationProvider | null;
    integrationId: string | null;
    name: string;
  }>({ open: false, provider: null, integrationId: null, name: '' });

  // Fetch integrations from backend
  const { data: backendIntegrations, isLoading } = useIntegrationsList(id);
  // Tagada Rx lives in its own tables (rx_*), not the generic integrations
  // table — its enabled state comes from the rx router.
  const rxStatus = useRxIntegrationStatus(id);
  const deleteMutation = useDeleteIntegration();

  const integrationsMetadata = useIntegrationsMetadata();

  // Merge backend data with metadata
  const mergedIntegrations = React.useMemo(() => {
    return (
      integrationsMetadata
        // Filter out tagadaOnly integrations for whitelabel
        .filter((metadata) => !metadata.tagadaOnly || isTagadaPay)
        // Merged but not open yet (Slash, #TAG-2955)
        .filter((metadata) => metadata.isAvailable !== false)
        // Tagada Rx: server-side white-label gate (direct merchant, or partner
        // with the TagadaRx line on its active signed CRM contract). A store
        // that already requested Rx keeps seeing it.
        .filter(
          (metadata) =>
            !metadata.rxGated ||
            rxStatus.data?.availableForAccount === true ||
            (rxStatus.data?.state !== undefined && rxStatus.data.state !== 'not_requested'),
        )
        .map((metadata) => {
          const backendData = backendIntegrations?.find(
            (integration) => integration.provider === metadata.id,
          );

          const enabled =
            metadata.id === 'tagada_rx'
              ? rxStatus.data?.state === 'active'
              : (backendData?.enabled ?? false);

          return {
            id: metadata.id,
            name:
              metadata.id === 'tagada_rx' && rxStatus.data
                ? rxStatus.data.rxName
                : t(`integrations:names.${metadata.id}`, metadata.name),
            description: t(`integrations:descriptions.${metadata.id}`, metadata.description),
            type: metadata.category,
            icon: metadata.icon,
            color: metadata.color,
            tags: metadata.tags,
            enabled,
            backendId: backendData?.id,
            logoUrl: metadata.logoUrl,
            externalUrl: metadata.externalUrl,
          };
        })
    );
  }, [
    backendIntegrations,
    isTagadaPay,
    t,
    integrationsMetadata,
    rxStatus.data?.state,
    rxStatus.data?.availableForAccount,
    rxStatus.data?.rxName,
  ]);

  // Filter integrations
  const filteredIntegrations = React.useMemo(() => {
    let filtered = mergedIntegrations;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((integration) => integration.type === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (integration) =>
          integration.name.toLowerCase().includes(query) ||
          integration.description.toLowerCase().includes(query) ||
          integration.tags.some((tag) => tag.toLowerCase().includes(query)),
      );
    }

    return filtered;
  }, [mergedIntegrations, selectedCategory, searchQuery]);

  // Count connected integrations
  const connectedCount = React.useMemo(() => {
    return mergedIntegrations.filter((integration) => integration.enabled).length;
  }, [mergedIntegrations]);

  // Navigation handlers
  const handleConfigure = (provider: IntegrationProvider) => {
    if (provider === 'custom_payment') {
      navigate(storeRoutes.INTEGRATION_CUSTOM_PAYMENT_HUB());
      return;
    }
    if (provider === 'tagada_rx') {
      navigate(storeRoutes.RX_OVERVIEW());
      return;
    }
    navigate(storeRoutes.INTEGRATION_CONFIG(provider));
  };

  const handleConnect = (provider: IntegrationProvider) => {
    if (provider === 'custom_payment') {
      navigate(storeRoutes.INTEGRATION_CUSTOM_PAYMENT_HUB());
      return;
    }
    if (provider === 'tagada_rx') {
      navigate(storeRoutes.RX_OVERVIEW());
      return;
    }
    navigate(storeRoutes.INTEGRATION_CONFIG(provider));
  };

  const handleDisconnect = (provider: IntegrationProvider) => {
    const integration = mergedIntegrations.find((i) => i.id === provider);
    if (!integration?.backendId) {
      toast({
        title: t('integrations:toasts.errorTitle'),
        description: t('integrations:toasts.integrationNotFound'),
        variant: 'destructive',
      });
      return;
    }
    setDisconnectDialog({
      open: true,
      provider,
      integrationId: integration.backendId,
      name: integration.name,
    });
  };

  const confirmDisconnect = async () => {
    if (!disconnectDialog.integrationId) return;

    try {
      await deleteMutation.mutateAsync({
        integrationId: disconnectDialog.integrationId,
      });

      toast({
        title: t('integrations:toasts.successTitle'),
        description: t('integrations:toasts.integrationDisconnected', { name: disconnectDialog.name }),
      });
    } catch (error) {
      console.error('Failed to disconnect integration:', error);
    } finally {
      setDisconnectDialog({ open: false, provider: null, integrationId: null, name: '' });
    }
  };

  return (
    <div className="flex flex-col space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{t('integrations:page.title')}</h1>
            {connectedCount > 0 && (
              <Badge variant="secondary" className="text-sm">
                {connectedCount} {t('integrations:page.connected')}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            {t('integrations:page.subtitle')}
          </p>
        </div>
        {isTagadaPay && (
          <Button onClick={() => setIsRequestDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('integrations:page.requestIntegration')}
          </Button>
        )}
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        {INTEGRATION_CATEGORIES.map((category) => (
          <Button
            key={category.id}
            variant={selectedCategory === category.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(category.id)}
            className="gap-2"
          >
            <span>{category.icon}</span>
            <span>{t(`integrations:categories.${category.id}`, category.name)}</span>
          </Button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder={t('integrations:page.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Results Count */}
      <div className="text-muted-foreground text-sm">
        {isLoading ? (
          <span>{t('integrations:page.loading')}</span>
        ) : (
          <span>
            {t('integrations:page.showing', { count: filteredIntegrations.length, total: mergedIntegrations.length })}
          </span>
        )}
      </div>

      {/* Crypto Helper Banner */}
      {isTagadaPay && !isLoading && !mergedIntegrations.find((i) => i.id === 'crypto' && i.enabled) && (
        <CryptoPaymentHelper to={storeRoutes.INTEGRATION_CONFIG('crypto')} />
      )}

      {/* Integrations Grid */}
      {isLoading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="bg-muted h-[300px] animate-pulse rounded-lg" />
          ))}
        </div>
      ) : filteredIntegrations.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredIntegrations.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onConfigure={() => handleConfigure(integration.id)}
              onConnect={() => handleConnect(integration.id)}
              onDisconnect={
                !integration.backendId ||
                integration.id === 'custom_payment' ||
                integration.id === 'tagada_rx'
                  ? undefined
                  : () => handleDisconnect(integration.id)
              }
            />
          ))}
        </div>
      ) : (
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="mx-auto flex max-w-[420px] flex-col items-center justify-center text-center">
            <div className="bg-muted flex h-20 w-20 items-center justify-center rounded-full">
              <Search className="text-muted-foreground h-10 w-10" />
            </div>
            <h3 className="mt-4 text-lg font-semibold">{t('integrations:page.noResults')}</h3>
            <p className="text-muted-foreground mb-4 mt-2 text-sm">
              {searchQuery
                ? t('integrations:page.noResultsSearch', { query: searchQuery })
                : t('integrations:page.noResultsCategory')}
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                {t('integrations:page.clearFilters')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Request Integration Dialog */}
      <RequestIntegrationDialog
        open={isRequestDialogOpen}
        onOpenChange={setIsRequestDialogOpen}
        storeId={id}
      />

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog
        open={disconnectDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDisconnectDialog({ open: false, provider: null, integrationId: null, name: '' });
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations:dialog.disconnectTitle', { name: disconnectDialog.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('integrations:dialog.disconnectDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('integrations:dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('integrations:dialog.disconnecting') : t('integrations:dialog.disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
